import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';
import axiosInstance from '@/api/axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { utils as xlsxUtils, writeFile as xlsxWriteFile } from 'xlsx/xlsx.mjs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
  Cell,
  ReferenceLine,
} from 'recharts';

// Custom Y-axis tick used by MIS Daily + Cumulative charts.
// Splits payload.value on "||" so we can show User on line 1 (dark bold)
// and Project on line 2 (purple italic). If the value has no "||" (idle
// user row) we render a single line.
const UserProjectTick = ({ x, y, payload }) => {
  const val = String(payload?.value ?? '');
  const [userLine, projectLine] = val.split('||');
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={-8}
        y={0}
        dy={projectLine ? -3 : 4}
        textAnchor="end"
        fill="#111827"
        fontSize={11}
        fontWeight={600}
      >
        {userLine}
      </text>
      {projectLine && (
        <text
          x={-8}
          y={0}
          dy={11}
          textAnchor="end"
          fill="#7C3AED"
          fontSize={10}
          fontStyle="italic"
        >
          {projectLine}
        </text>
      )}
    </g>
  );
};

// Shared tooltip styling for MIS charts.
const misTooltipContentStyle = {
  fontSize: 12,
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  boxShadow: '0 6px 20px rgba(17, 24, 39, 0.08)',
  padding: '8px 12px',
};

const misTooltipCursor = { fill: 'rgba(79, 70, 229, 0.06)' };

// Utilisation-band colors used by MIS Report 2 (Scribing Utilisation).
// Buckets are relative to a per-scribe benchmark of 8h × working days
// (Mon–Fri) in the selected month up to the end production date.
const UTIL_COLORS = {
  over: '#F59E0B',   // > 110% — verify logs
  onTarget: '#22C55E', // 80 – 110%
  mid: '#3B82F6',     // 50 – 80%
  under: '#EF4444',   // 0.01 – 50%
  zero: '#D1D5DB',    // exactly 0 hours
};
const utilColorFor = (hours, target) => {
  if (hours <= 0) return UTIL_COLORS.zero;
  const pct = target > 0 ? (hours / target) * 100 : 0;
  if (pct > 110) return UTIL_COLORS.over;
  if (pct >= 80) return UTIL_COLORS.onTarget;
  if (pct >= 50) return UTIL_COLORS.mid;
  return UTIL_COLORS.under;
};

// Count Monday–Friday days between two Dates, inclusive.
const countWeekdays = (from, to) => {
  let n = 0;
  const cursor = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  while (cursor <= end) {
    const d = cursor.getDay();
    if (d >= 1 && d <= 5) n += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return n;
};

const ReportsPanel = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Report 1 states
  const [report1Data, setReport1Data] = useState([]);
  const [report1Summary, setReport1Summary] = useState(null);
  const [report1Filters, setReport1Filters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    project: '',
  });
  
  // Report 2 states
  const [report2Data, setReport2Data] = useState([]);
  const [report2Filters, setReport2Filters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });
  const [report2Summary, setReport2Summary] = useState({
    totalDurationHours: 0,
    totalDurationMins: 0,
    totalPreChartHours: 0,
    totalPreChartMins: 0,
  });

  // Report 3 states (User Report)
  const [report3Data, setReport3Data] = useState([]);
  const [report3Summary, setReport3Summary] = useState(null);
  const [report3Filters, setReport3Filters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    user: '',
  });

  // Report 4 states (Daily Report - includes all active users)
  const [report4Rows, setReport4Rows] = useState([]);
  const [report4Summary, setReport4Summary] = useState(null);
  const [report4Filters, setReport4Filters] = useState({
    date: new Date().toISOString().split('T')[0],
  });
  const report4ChartRef = useRef(null);

  // Report 5 states (Cumulative Report - monthly per-user totals)
  const [report5Rows, setReport5Rows] = useState([]);
  const [report5Summary, setReport5Summary] = useState(null);
  const [report5Filters, setReport5Filters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });
  const report5ChartRef = useRef(null);

  // Report 6 states (MIS - Project Wise Cumulative)
  const [report6Rows, setReport6Rows] = useState([]);
  const report6ChartRef = useRef(null);

  // Report 7 states (MIS Report 2 - Scribing Utilisation)
  const [report7Rows, setReport7Rows] = useState([]);
  const [report7Summary, setReport7Summary] = useState(null);
  const [report7Filters, setReport7Filters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    date: new Date().toISOString().split('T')[0],
  });
  const report7ChartRef = useRef(null);

  useEffect(() => {
    fetchProjects();
    fetchUsers();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axiosInstance.get('/masters/projects');
      // Sort projects alphabetically
      const sortedProjects = response.data.sort((a, b) => 
        a.project_name.localeCompare(b.project_name)
      );
      setProjects(sortedProjects);
    } catch (error) {
      toast.error('Failed to load projects');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/masters/users');
      // Sort users alphabetically
      const sortedUsers = response.data.sort((a, b) => 
        a.user_name.localeCompare(b.user_name)
      );
      setUsers(sortedUsers);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const calculateTimeDifference = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return { hours, mins };
  };

  const checkExceedsTenHours = (entry) => {
    const start = new Date(entry.start_time);
    const end = new Date(entry.end_time);
    const durationMins = (end - start) / 60000;
    const preChartMins = entry.pre_charting_Hours * 60 + entry.pre_charting_mins;
    const totalMins = durationMins + preChartMins;
    return totalMins > 600; // 10 hours = 600 minutes
  };

  const generateReport1 = async () => {
    if (!report1Filters.project) {
      toast.error('Please select a project');
      return;
    }

    try {
      const response = await axiosInstance.get('/scribe');
      const allData = response.data;

      // Filter by month, year, and project
      const filtered = allData
        .filter((entry) => {
          const startDate = new Date(entry.start_time);
          return (
            startDate.getMonth() + 1 === parseInt(report1Filters.month) &&
            startDate.getFullYear() === parseInt(report1Filters.year) &&
            entry.project === report1Filters.project
          );
        })
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

      // Calculate totals
      let totalDurationMins = 0;
      let totalPreChartMins = 0;

      filtered.forEach((entry) => {
        const duration = calculateTimeDifference(entry.start_time, entry.end_time);
        const durationMins = duration.hours * 60 + duration.mins;
        const preChartMins = entry.pre_charting_Hours * 60 + entry.pre_charting_mins;
        
        totalDurationMins += durationMins;
        totalPreChartMins += preChartMins;
      });

      // Store totals for Report 1
      const report1Totals = {
        count: filtered.length,
        totalDurationHours: Math.floor(totalDurationMins / 60),
        totalDurationMins: totalDurationMins % 60,
        totalPreChartHours: Math.floor(totalPreChartMins / 60),
        totalPreChartMins: totalPreChartMins % 60,
        totalDecimal: ((totalDurationMins + totalPreChartMins) / 60).toFixed(2)
      };

      setReport1Data(filtered);
      setReport1Summary(report1Totals);
      toast.success(`Report generated: ${filtered.length} entries found`);
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };

  const generateReport2 = async () => {
    try {
      const response = await axiosInstance.get('/scribe');
      const allData = response.data;

      // Filter by month and year
      const filtered = allData
        .filter((entry) => {
          const startDate = new Date(entry.start_time);
          return (
            startDate.getMonth() + 1 === parseInt(report2Filters.month) &&
            startDate.getFullYear() === parseInt(report2Filters.year)
          );
        })
        .sort((a, b) => {
          // Sort by project first, then start_time
          if (a.project !== b.project) {
            return a.project.localeCompare(b.project);
          }
          return new Date(a.start_time) - new Date(b.start_time);
        });

      // Calculate overall summary and project subtotals
      let totalDurationMins = 0;
      let totalPreChartMins = 0;
      const projectSubtotals = {};
      const projectsWithData = new Set();

      filtered.forEach((entry) => {
        const duration = calculateTimeDifference(entry.start_time, entry.end_time);
        const durationMins = duration.hours * 60 + duration.mins;
        const preChartMins = entry.pre_charting_Hours * 60 + entry.pre_charting_mins;
        
        totalDurationMins += durationMins;
        totalPreChartMins += preChartMins;

        // Track projects with data
        projectsWithData.add(entry.project);

        // Calculate project subtotals
        if (!projectSubtotals[entry.project]) {
          projectSubtotals[entry.project] = {
            durationMins: 0,
            preChartMins: 0,
            count: 0
          };
        }
        projectSubtotals[entry.project].durationMins += durationMins;
        projectSubtotals[entry.project].preChartMins += preChartMins;
        projectSubtotals[entry.project].count += 1;
      });

      // Find projects with no data
      const projectsWithNoData = projects
        .filter(p => !projectsWithData.has(p.project_name))
        .map(p => p.project_name)
        .sort((a, b) => a.localeCompare(b));

      setReport2Summary({
        totalDurationHours: Math.floor(totalDurationMins / 60),
        totalDurationMins: totalDurationMins % 60,
        totalPreChartHours: Math.floor(totalPreChartMins / 60),
        totalPreChartMins: totalPreChartMins % 60,
        projectSubtotals: projectSubtotals,
        projectsWithNoData: projectsWithNoData
      });

      setReport2Data(filtered);
      toast.success(`Report generated: ${filtered.length} entries found, ${projectsWithNoData.length} projects with no data`);
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };

  const generateReport3 = async () => {
    if (!report3Filters.user) {
      toast.error('Please select a user');
      return;
    }

    try {
      const response = await axiosInstance.get('/scribe');
      const allData = response.data;

      // Filter by month, year, and user
      const filtered = allData
        .filter((entry) => {
          const startDate = new Date(entry.start_time);
          return (
            startDate.getMonth() + 1 === parseInt(report3Filters.month) &&
            startDate.getFullYear() === parseInt(report3Filters.year) &&
            entry.user === report3Filters.user
          );
        })
        .sort((a, b) => a.project.localeCompare(b.project)); // Sort by project

      // Calculate totals
      let totalDurationMins = 0;
      let totalPreChartMins = 0;

      filtered.forEach((entry) => {
        const duration = calculateTimeDifference(entry.start_time, entry.end_time);
        const durationMins = duration.hours * 60 + duration.mins;
        const preChartMins = entry.pre_charting_Hours * 60 + entry.pre_charting_mins;
        
        totalDurationMins += durationMins;
        totalPreChartMins += preChartMins;
      });

      // Store totals for Report 3
      const report3Totals = {
        count: filtered.length,
        totalDurationHours: Math.floor(totalDurationMins / 60),
        totalDurationMins: totalDurationMins % 60,
        totalPreChartHours: Math.floor(totalPreChartMins / 60),
        totalPreChartMins: totalPreChartMins % 60,
        totalDecimal: ((totalDurationMins + totalPreChartMins) / 60).toFixed(2)
      };

      setReport3Data(filtered);
      setReport3Summary(report3Totals);
      toast.success(`Report generated: ${filtered.length} entries found`);
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };

  // Daily Report — one row per user (blank row if user did not work that day).
  // If a user has multiple entries on the same day, each entry gets its own row.
  const generateReport4 = async () => {
    if (!report4Filters.date) {
      toast.error('Please select a date');
      return;
    }

    try {
      const response = await axiosInstance.get('/scribe');
      const allData = response.data;

      // Filter entries for the selected calendar date (matches start_time's local date)
      const target = report4Filters.date; // YYYY-MM-DD
      const entriesForDay = allData.filter((entry) => {
        const d = new Date(entry.start_time);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        if (`${y}-${m}-${dd}` !== target) return false;
        return true;
      });

      // Group entries by user
      const entriesByUser = {};
      entriesForDay.forEach((e) => {
        if (!entriesByUser[e.user]) entriesByUser[e.user] = [];
        entriesByUser[e.user].push(e);
      });

      // Sort ACTIVE users only alphabetically (inactive users are excluded from MIS Report)
      const sortedUsers = [...users]
        .filter((u) => u.active !== false)
        .map((u) => u.user_name)
        .sort((a, b) => a.localeCompare(b));

      // Build the rows: for each user, either their entries or a single blank row
      const rows = [];
      let totalDurationMins = 0;
      let totalPreChartMins = 0;
      let workedUsers = 0;
      let idleUsers = 0;

      sortedUsers.forEach((userName) => {
        const userEntries = entriesByUser[userName] || [];
        if (userEntries.length === 0) {
          rows.push({ user: userName, empty: true });
          idleUsers += 1;
        } else {
          workedUsers += 1;
          userEntries
            .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
            .forEach((e) => {
              rows.push({ ...e, empty: false });
              const dur = calculateTimeDifference(e.start_time, e.end_time);
              totalDurationMins += dur.hours * 60 + dur.mins;
              totalPreChartMins += e.pre_charting_Hours * 60 + e.pre_charting_mins;
            });
        }
      });

      setReport4Rows(rows);
      setReport4Summary({
        totalEntries: entriesForDay.length,
        totalUsers: sortedUsers.length,
        workedUsers,
        idleUsers,
        totalDurationHours: Math.floor(totalDurationMins / 60),
        totalDurationMins: totalDurationMins % 60,
        totalPreChartHours: Math.floor(totalPreChartMins / 60),
        totalPreChartMins: totalPreChartMins % 60,
        totalDecimal: ((totalDurationMins + totalPreChartMins) / 60).toFixed(2),
      });
      toast.success(
        `Report generated: ${workedUsers} user(s) worked, ${idleUsers} idle`
      );
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };


  // Cumulative Report — one row per user with month-total Duration + Pre-charting.
  // Users with no entries in the month appear with a blank row.
  const generateReport5 = async () => {
    try {
      const response = await axiosInstance.get('/scribe');
      const allData = response.data;

      const month = parseInt(report5Filters.month);
      const year = parseInt(report5Filters.year);

      const entriesForMonth = allData.filter((entry) => {
        const d = new Date(entry.start_time);
        if (d.getFullYear() !== year || d.getMonth() + 1 !== month) return false;
        return true;
      });

      // Aggregate by (user, project)
      const byUserProject = {};
      entriesForMonth.forEach((e) => {
        const key = `${e.user}|${e.project}`;
        if (!byUserProject[key]) {
          byUserProject[key] = {
            user: e.user,
            project: e.project,
            durationMins: 0,
            preChartMins: 0,
            count: 0,
          };
        }
        const dur = calculateTimeDifference(e.start_time, e.end_time);
        byUserProject[key].durationMins += dur.hours * 60 + dur.mins;
        byUserProject[key].preChartMins +=
          e.pre_charting_Hours * 60 + e.pre_charting_mins;
        byUserProject[key].count += 1;
      });

      const sortedUserNames = [...users]
        .filter((u) => u.active !== false)
        .map((u) => u.user_name)
        .sort((a, b) => a.localeCompare(b));

      const rows = [];
      let workedUsers = 0;
      let idleUsers = 0;
      let totalDurationMins = 0;
      let totalPreChartMins = 0;

      sortedUserNames.forEach((userName) => {
        const userEntries = Object.values(byUserProject).filter(
          (x) => x.user === userName
        );
        if (userEntries.length === 0) {
          rows.push({ user: userName, empty: true });
          idleUsers += 1;
        } else {
          workedUsers += 1;
          userEntries
            .sort((a, b) => a.project.localeCompare(b.project))
            .forEach((agg) => {
              totalDurationMins += agg.durationMins;
              totalPreChartMins += agg.preChartMins;
              rows.push({
                user: userName,
                project: agg.project,
                empty: false,
                durationMins: agg.durationMins,
                preChartMins: agg.preChartMins,
                count: agg.count,
              });
            });
        }
      });

      setReport5Rows(rows);
      setReport5Summary({
        totalEntries: entriesForMonth.length,
        totalUsers: sortedUserNames.length,
        workedUsers,
        idleUsers,
        totalDurationHours: Math.floor(totalDurationMins / 60),
        totalDurationMins: totalDurationMins % 60,
        totalPreChartHours: Math.floor(totalPreChartMins / 60),
        totalPreChartMins: totalPreChartMins % 60,
        totalDecimal: ((totalDurationMins + totalPreChartMins) / 60).toFixed(2),
      });

      toast.success(
        `Report generated: ${workedUsers} user(s) worked, ${idleUsers} idle`
      );
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };


  // Project Wise Cumulative — one row per ACTIVE project with month-total hours.
  const generateReport6 = async () => {
    try {
      const response = await axiosInstance.get('/scribe');
      const allData = response.data;

      const month = parseInt(report5Filters.month);
      const year = parseInt(report5Filters.year);

      const entries = allData.filter((entry) => {
        const d = new Date(entry.start_time);
        return d.getFullYear() === year && d.getMonth() + 1 === month;
      });

      const byProject = {};
      entries.forEach((e) => {
        if (!byProject[e.project]) {
          byProject[e.project] = { durationMins: 0, preChartMins: 0, count: 0 };
        }
        const dur = calculateTimeDifference(e.start_time, e.end_time);
        byProject[e.project].durationMins += dur.hours * 60 + dur.mins;
        byProject[e.project].preChartMins +=
          e.pre_charting_Hours * 60 + e.pre_charting_mins;
        byProject[e.project].count += 1;
      });

      const activeProjectNames = [...projects]
        .filter((p) => p.active !== false)
        .map((p) => p.project_name)
        .sort((a, b) => a.localeCompare(b));

      const rows = activeProjectNames.map((projectName) => {
        const agg = byProject[projectName];
        return agg
          ? { project: projectName, empty: false, ...agg }
          : { project: projectName, empty: true };
      });

      setReport6Rows(rows);
    } catch (error) {
      toast.error('Failed to generate project-wise report');
    }
  };

  // Report 7 — MIS Report 2 — Scribing Utilisation
  //   MTD hours per active user, sorted high-to-low.
  //   Colour band vs 120 h benchmark (8 h × 15 working days).
  //   Also computes summary tiles: MTD hours, capacity used %,
  //   zero-hour scribes MTD, and zero-hour scribes for the reference date.
  const generateReport7 = async () => {
    if (!report7Filters.date) {
      toast.error('Please select an end production date');
      return;
    }
    try {
      const response = await axiosInstance.get('/scribe');
      const allData = response.data;

      const year = parseInt(report7Filters.year);
      const month = parseInt(report7Filters.month);

      // Effective as-of cutoff:
      //  - if selected date falls in the selected month/year -> use it
      //  - otherwise -> last day of the selected month.
      const [dyStr, dmStr, ddStr] = report7Filters.date.split('-');
      const dY = parseInt(dyStr);
      const dM = parseInt(dmStr);
      const dD = parseInt(ddStr);
      const dateInMonth = dY === year && dM === month;
      const asOfDay = dateInMonth ? dD : new Date(year, month, 0).getDate();
      const asOfDate = new Date(year, month - 1, asOfDay, 23, 59, 59, 999);
      const asOfKey = `${year}-${String(month).padStart(2, '0')}-${String(asOfDay).padStart(2, '0')}`;

      // Filter to entries in the selected month AND up to the as-of date.
      const entriesForMonth = allData.filter((entry) => {
        const d = new Date(entry.start_time);
        if (d.getFullYear() !== year || d.getMonth() + 1 !== month) return false;
        return d <= asOfDate;
      });

      // Aggregate by user: total hours (Duration + Pre-charting) and set of projects.
      const byUser = {};
      entriesForMonth.forEach((e) => {
        if (!byUser[e.user]) {
          byUser[e.user] = { totalMins: 0, projects: new Set(), productionOnAsOfDayMins: 0 };
        }
        const dur = calculateTimeDifference(e.start_time, e.end_time);
        const entryProdMins =
          dur.hours * 60 + dur.mins + e.pre_charting_Hours * 60 + e.pre_charting_mins;
        byUser[e.user].totalMins += entryProdMins;
        if (e.project) byUser[e.user].projects.add(e.project);
        const d = new Date(e.start_time);
        const ek = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (ek === asOfKey) byUser[e.user].productionOnAsOfDayMins += entryProdMins;
      });

      const activeUsers = [...users]
        .filter((u) => u.active !== false)
        .map((u) => u.user_name);

      // Dynamic benchmark: 8 hrs × Mon-Fri days from day 1 to the as-of date.
      const totalScribes = activeUsers.length;
      const workingDays = countWeekdays(
        new Date(year, month - 1, 1),
        asOfDate
      );
      const benchmarkPerScribe = 8 * workingDays;
      const capacityDenominator = totalScribes * benchmarkPerScribe;

      // One row per active user, even if idle.
      const rowsUnsorted = activeUsers.map((userName) => {
        const agg = byUser[userName];
        const hours = agg ? Number((agg.totalMins / 60).toFixed(2)) : 0;
        const projectCount = agg ? agg.projects.size : 0;
        const producedOnAsOfDay = agg ? agg.productionOnAsOfDayMins > 0 : false;
        const pct = benchmarkPerScribe > 0
          ? Math.round((hours / benchmarkPerScribe) * 100)
          : 0;
        return {
          user: userName,
          projectCount,
          label:
            projectCount > 1 ? `${userName} (${projectCount})` : userName,
          hours,
          pct,
          barLabel: hours > 0 ? `${hours} (${pct}%)` : '',
          fill: utilColorFor(hours, benchmarkPerScribe),
          producedOnAsOfDay,
        };
      });

      // Sort high-to-low, alphabetical tiebreak.
      const sorted = rowsUnsorted.sort((a, b) => {
        if (b.hours !== a.hours) return b.hours - a.hours;
        return a.user.localeCompare(b.user);
      });

      // Summary tiles.
      const mtdHours = Number(
        sorted.reduce((sum, r) => sum + r.hours, 0).toFixed(2)
      );
      const capacityUsedPct =
        capacityDenominator > 0
          ? Math.round((mtdHours / capacityDenominator) * 100)
          : 0;
      const zeroMtd = sorted.filter((r) => r.hours <= 0).length;
      const zeroOnAsOf = sorted.filter((r) => !r.producedOnAsOfDay).length;

      setReport7Rows(sorted);
      setReport7Summary({
        mtdHours,
        capacityUsedPct,
        capacityDenominator,
        totalScribes,
        workingDays,
        benchmarkPerScribe,
        zeroMtd,
        zeroOnAsOf,
        asOfDate,
        asOfLabel: asOfDate.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
        }),
        month,
        year,
      });
      toast.success(`Utilisation report generated for ${totalScribes} scribes`);
    } catch (error) {
      toast.error('Failed to generate utilisation report');
    }
  };

  const exportReport7ToPDF = async () => {
    if (!report7ChartRef?.current || report7Rows.length === 0) {
      toast.error('Generate the report first');
      return;
    }
    try {
      const canvas = await html2canvas(report7ChartRef.current, {
        backgroundColor: '#ffffff',
        scale: 1,
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.85);
      const doc = new jsPDF({ orientation: 'portrait' });
      const pageWidth = doc.internal.pageSize.width;
      const innerWidth = pageWidth - 20;
      const imgHeight = (canvas.height * innerWidth) / canvas.width;
      const maxHeight = doc.internal.pageSize.height - 20;
      const finalHeight = Math.min(imgHeight, maxHeight);
      const finalWidth = (canvas.width * finalHeight) / canvas.height;
      const xOffset = (pageWidth - finalWidth) / 2;
      doc.addImage(imgData, 'JPEG', xOffset, 10, finalWidth, finalHeight);
      doc.save(`MIS_Report_2_${report7Filters.date}.pdf`);
      toast.success('PDF downloaded successfully');
    } catch (e) {
      toast.error('Failed to export PDF');
    }
  };

  const exportMISToPDF = async () => {
    if (report4Rows.length === 0 && report5Rows.length === 0) {
      toast.error('Generate the report first');
      return;
    }

    const doc = new jsPDF({ orientation: 'portrait' });
    const pageWidth = doc.internal.pageSize.width;
    const monthLabel = months.find((m) => m.value === parseInt(report5Filters.month))?.label;
    let firstPage = true;

    const drawChart = async (ref, title) => {
      if (!ref?.current) return;
      try {
        const canvas = await html2canvas(ref.current, {
          backgroundColor: '#ffffff',
          scale: 1,
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.85);
        if (!firstPage) doc.addPage();
        firstPage = false;

        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text(title, pageWidth / 2, 20, { align: 'center' });

        const pageInnerWidth = pageWidth - 20;
        const imgHeight = (canvas.height * pageInnerWidth) / canvas.width;
        const maxHeight = doc.internal.pageSize.height - 30;
        const finalHeight = Math.min(imgHeight, maxHeight);
        const finalWidth = (canvas.width * finalHeight) / canvas.height;
        const xOffset = (pageWidth - finalWidth) / 2;
        doc.addImage(imgData, 'JPEG', xOffset, 28, finalWidth, finalHeight);
      } catch (e) {
        // capture failure — skip this chart
      }
    };

    await drawChart(
      report4ChartRef,
      `Daily Report Graph — ${report4Filters.date}`
    );
    await drawChart(
      report5ChartRef,
      `Cumulative Report Graph — ${monthLabel} ${report5Filters.year}`
    );
    await drawChart(
      report6ChartRef,
      `Project Wise Cumulative — ${monthLabel} ${report5Filters.year}`
    );

    doc.save(`MIS_Report_${report4Filters.date}.pdf`);
    toast.success('PDF downloaded successfully');
  };

  const exportToPDF = (reportData, reportName, isReport2 = false, summary = null) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Title
    doc.setFontSize(18);
    doc.text(reportName, pageWidth / 2, 20, { align: 'center' });
    
    // Subtitle with filters
    doc.setFontSize(10);
    if (isReport2) {
      const monthName = months.find(m => m.value === report2Filters.month)?.label;
      doc.text(`Month: ${monthName} ${report2Filters.year}`, pageWidth / 2, 28, { align: 'center' });
    } else {
      const monthName = months.find(m => m.value === report1Filters.month)?.label;
      const filterInfo = report1Filters.project 
        ? `Project: ${report1Filters.project} | Month: ${monthName} ${report1Filters.year}`
        : report3Filters.user
        ? `User: ${report3Filters.user} | Month: ${monthName} ${report3Filters.year}`
        : `Month: ${monthName} ${report1Filters.year}`;
      doc.text(filterInfo, pageWidth / 2, 28, { align: 'center' });
    }
    
    let startY = 35;
    
    // Prepare table data
    const tableData = [];
    
    reportData.forEach((entry, index) => {
      const duration = calculateTimeDifference(entry.start_time, entry.end_time);
      
      tableData.push([
        entry.project,
        entry.user,
        new Date(entry.start_time).toLocaleString(),
        new Date(entry.end_time).toLocaleString(),
        `${duration.hours}h ${duration.mins}m`,
        `${entry.pre_charting_Hours}h ${entry.pre_charting_mins}m`,
        entry.prior_authorization ? 'Yes' : 'No'
      ]);
      
      // Add subtotal row for Report 2
      if (isReport2 && summary?.projectSubtotals) {
        const isLastInGroup = index === reportData.length - 1 || 
                            (index < reportData.length - 1 && reportData[index + 1].project !== entry.project);
        
        if (isLastInGroup && summary.projectSubtotals[entry.project]) {
          const subtotal = summary.projectSubtotals[entry.project];
          const totalHoursDecimal = ((subtotal.durationMins + subtotal.preChartMins) / 60).toFixed(2);
          
          tableData.push([
            { content: `SUBTOTAL: ${entry.project} (${subtotal.count} entries)`, colSpan: 3, styles: { fontStyle: 'bold', fillColor: [240, 240, 255] } },
            { content: `Total: ${totalHoursDecimal} hrs`, styles: { fontStyle: 'bold', fillColor: [240, 240, 255], textColor: [49, 46, 129] } },
            { content: `${Math.floor(subtotal.durationMins / 60)}h ${subtotal.durationMins % 60}m`, styles: { fontStyle: 'bold', fillColor: [240, 240, 255] } },
            { content: `${Math.floor(subtotal.preChartMins / 60)}h ${subtotal.preChartMins % 60}m`, styles: { fontStyle: 'bold', fillColor: [240, 240, 255] } },
            { content: '', styles: { fillColor: [240, 240, 255] } }
          ]);
        }
      }
    });
    
    // Table
    autoTable(doc, {
      startY: startY,
      head: [['Project', 'User', 'Start Time', 'End Time', 'Duration', 'Pre-chart', 'Auth']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [49, 46, 129], textColor: 255 },
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 25 },
        2: { cellWidth: 35 },
        3: { cellWidth: 35 },
        4: { cellWidth: 20 },
        5: { cellWidth: 20 },
        6: { cellWidth: 15 }
      }
    });
    
    // Add summary for both reports
    if (summary) {
      const finalY = doc.lastAutoTable.finalY + 10;
      
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      if (isReport2) {
        doc.text('OVERALL SUMMARY', 14, finalY);
      } else {
        doc.text('PROJECT SUMMARY', 14, finalY);
      }
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      
      if (isReport2) {
        doc.text(`Total Duration: ${summary.totalDurationHours}h ${summary.totalDurationMins}m`, 14, finalY + 8);
        doc.text(`Total Pre-charting: ${summary.totalPreChartHours}h ${summary.totalPreChartMins}m`, 14, finalY + 16);
        doc.text(`Total Entries: ${reportData.length}`, 14, finalY + 24);
        
        // Calculate combined total
        const totalMins = (summary.totalDurationHours * 60 + summary.totalDurationMins + 
                          summary.totalPreChartHours * 60 + summary.totalPreChartMins);
        const combinedTotal = (totalMins / 60).toFixed(2);
        doc.text(`Combined Total: ${combinedTotal} hours`, 14, finalY + 32);
        
        // Add projects with no data
        if (summary.projectsWithNoData && summary.projectsWithNoData.length > 0) {
          doc.setFontSize(12);
          doc.setFont(undefined, 'bold');
          doc.text('Projects with No Data:', 14, finalY + 48);
          
          doc.setFontSize(9);
          doc.setFont(undefined, 'normal');
          
          let yPos = finalY + 56;
          summary.projectsWithNoData.forEach((project, index) => {
            // Check if we need a new page
            if (yPos > 270) {
              doc.addPage();
              yPos = 20;
            }
            doc.text(`• ${project}`, 14, yPos);
            yPos += 6;
          });
          
          doc.setFontSize(10);
          doc.setFont(undefined, 'italic');
          doc.text(`${summary.projectsWithNoData.length} project(s) with no activity during this period`, 14, yPos + 4);
        }
      } else {
        // Report 1 or 3 summary
        doc.text(`Total Entries: ${summary.count}`, 14, finalY + 8);
        doc.text(`Total Duration: ${summary.totalDurationHours}h ${summary.totalDurationMins}m`, 14, finalY + 16);
        doc.text(`Total Pre-charting: ${summary.totalPreChartHours}h ${summary.totalPreChartMins}m`, 14, finalY + 24);
        doc.text(`Combined Total: ${summary.totalDecimal} hours`, 14, finalY + 32);
      }
    }
    
    // Save
    doc.save(`${reportName}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF downloaded successfully');
  };

  const exportToCSV = (reportData, reportName) => {
    // Convert to CSV
    const headers = [
      'Project',
      'User',
      'Start Time',
      'End Time',
      'Duration (Hours)',
      'Duration (Minutes)',
      'Pre-chart Hours',
      'Pre-chart Minutes',
      'Prior Authorization',
    ];

    const csvContent = [
      headers.join(','),
      ...reportData.map((entry) => {
        const duration = calculateTimeDifference(entry.start_time, entry.end_time);
        return [
          `"${entry.project}"`,
          `"${entry.user}"`,
          new Date(entry.start_time).toLocaleString(),
          new Date(entry.end_time).toLocaleString(),
          duration.hours,
          duration.mins,
          entry.pre_charting_Hours,
          entry.pre_charting_mins,
          entry.prior_authorization ? 'Yes' : 'No',
        ].join(',');
      }),
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reportName}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success('CSV exported successfully');
  };

  const exportMonthlyToExcel = (reportData, reportName, summary) => {
    // Monthly Report Excel — mirrors PDF layout with project-wise subtotals,
    // overall summary section, and "Projects with no data" section.
    const monthName = months.find((m) => m.value === report2Filters.month)?.label;
    const filterLine = `Month: ${monthName} ${report2Filters.year}`;

    const headers = [
      'Project',
      'User',
      'Start Time',
      'End Time',
      'Duration',
      'Pre-chart',
      'Auth',
    ];

    // Build AOA (array of arrays) so we can intermix subtotal rows + summary section
    const aoa = [];
    aoa.push([reportName.replace(/_/g, ' ')]);
    aoa.push([filterLine]);
    aoa.push([]);
    aoa.push(headers);

    // Detail rows + project subtotals
    reportData.forEach((entry, index) => {
      const duration = calculateTimeDifference(entry.start_time, entry.end_time);
      aoa.push([
        entry.project,
        entry.user,
        new Date(entry.start_time).toLocaleString(),
        new Date(entry.end_time).toLocaleString(),
        `${duration.hours}h ${duration.mins}m`,
        `${entry.pre_charting_Hours}h ${entry.pre_charting_mins}m`,
        entry.prior_authorization ? 'Yes' : 'No',
      ]);

      if (summary?.projectSubtotals) {
        const isLastInGroup =
          index === reportData.length - 1 ||
          (index < reportData.length - 1 &&
            reportData[index + 1].project !== entry.project);

        if (isLastInGroup && summary.projectSubtotals[entry.project]) {
          const subtotal = summary.projectSubtotals[entry.project];
          const totalHoursDecimal = (
            (subtotal.durationMins + subtotal.preChartMins) /
            60
          ).toFixed(2);
          aoa.push([
            `SUBTOTAL: ${entry.project} (${subtotal.count} entries)`,
            '',
            '',
            `Total: ${totalHoursDecimal} hrs`,
            `${Math.floor(subtotal.durationMins / 60)}h ${
              subtotal.durationMins % 60
            }m`,
            `${Math.floor(subtotal.preChartMins / 60)}h ${
              subtotal.preChartMins % 60
            }m`,
            '',
          ]);
        }
      }
    });

    // Overall summary section
    if (summary) {
      aoa.push([]);
      aoa.push(['OVERALL SUMMARY']);
      aoa.push([
        'Total Duration',
        `${summary.totalDurationHours}h ${summary.totalDurationMins}m`,
      ]);
      aoa.push([
        'Total Pre-charting',
        `${summary.totalPreChartHours}h ${summary.totalPreChartMins}m`,
      ]);
      aoa.push(['Total Entries', reportData.length]);

      const totalMins =
        summary.totalDurationHours * 60 +
        summary.totalDurationMins +
        summary.totalPreChartHours * 60 +
        summary.totalPreChartMins;
      const combinedTotal = (totalMins / 60).toFixed(2);
      aoa.push(['Combined Total (hrs)', combinedTotal]);

      // Projects with no data
      if (summary.projectsWithNoData && summary.projectsWithNoData.length > 0) {
        aoa.push([]);
        aoa.push(['Projects with No Data']);
        summary.projectsWithNoData.forEach((p) => aoa.push([p]));
        aoa.push([
          `${summary.projectsWithNoData.length} project(s) with no activity during this period`,
        ]);
      }
    }

    const ws = xlsxUtils.aoa_to_sheet(aoa);

    // Column widths
    ws['!cols'] = [
      { wch: 30 },
      { wch: 22 },
      { wch: 22 },
      { wch: 22 },
      { wch: 14 },
      { wch: 14 },
      { wch: 8 },
    ];

    // Merge title + subtitle across header columns
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } },
    ];

    const wb = xlsxUtils.book_new();
    xlsxUtils.book_append_sheet(wb, ws, 'Monthly Report');

    const filename = `${reportName}_${new Date()
      .toISOString()
      .split('T')[0]}.xlsx`;
    xlsxWriteFile(wb, filename);
    toast.success('Excel exported successfully');
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const ReportTable = ({ data, showSubtotals = false, subtotals = {} }) => {
    return (
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
              Project
            </TableHead>
            <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
              User
            </TableHead>
            <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
              Start Time
            </TableHead>
            <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
              End Time
            </TableHead>
            <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
              Duration
            </TableHead>
            <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
              Pre-chart
            </TableHead>
            <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">
              Auth
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                No data found. Generate a report to see results.
              </TableCell>
            </TableRow>
          ) : (
            <>
              {data.map((detail, index) => {
                const duration = calculateTimeDifference(detail.start_time, detail.end_time);
                const exceedsTenHours = checkExceedsTenHours(detail);
                
                // Check if this is the last entry or next entry has different project
                const isLastInGroup = showSubtotals && (
                  index === data.length - 1 || 
                  (index < data.length - 1 && data[index + 1].project !== detail.project)
                );
                
                return (
                  <React.Fragment key={index}>
                    <TableRow className={`border-b hover:bg-muted/50 ${exceedsTenHours ? 'bg-yellow-50 border-l-4 border-l-yellow-500' : ''}`}>
                      <TableCell className="px-2 py-3 text-sm">{detail.project}</TableCell>
                      <TableCell className="px-2 py-3 text-sm">{detail.user}</TableCell>
                      <TableCell className="px-2 py-3 text-sm">
                        {new Date(detail.start_time).toLocaleString()}
                      </TableCell>
                      <TableCell className="px-2 py-3 text-sm">
                        {new Date(detail.end_time).toLocaleString()}
                      </TableCell>
                      <TableCell className="px-2 py-3 text-sm font-medium">
                        {duration.hours}h {duration.mins}m
                        {exceedsTenHours && (
                          <span className="ml-2 text-xs bg-yellow-500 text-white px-2 py-0.5 rounded">
                            &gt;10hrs
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="px-2 py-3 text-sm">
                        {detail.pre_charting_Hours}h {detail.pre_charting_mins}m
                      </TableCell>
                      <TableCell className="px-2 py-3 text-sm">
                        {detail.prior_authorization ? 'Yes' : 'No'}
                      </TableCell>
                    </TableRow>
                    
                    {isLastInGroup && subtotals[detail.project] && (
                      <TableRow className="bg-primary/5 font-medium border-b-2 border-primary/20">
                        <TableCell colSpan={3} className="px-2 py-2 text-sm">
                          <strong>Subtotal for {detail.project}</strong> ({subtotals[detail.project].count} entries)
                        </TableCell>
                        <TableCell className="px-2 py-2 text-sm font-semibold text-primary">
                          Total: {((subtotals[detail.project].durationMins + subtotals[detail.project].preChartMins) / 60).toFixed(2)} hrs
                        </TableCell>
                        <TableCell className="px-2 py-2 text-sm font-semibold text-primary">
                          {Math.floor(subtotals[detail.project].durationMins / 60)}h {subtotals[detail.project].durationMins % 60}m
                        </TableCell>
                        <TableCell className="px-2 py-2 text-sm font-semibold text-primary">
                          {Math.floor(subtotals[detail.project].preChartMins / 60)}h {subtotals[detail.project].preChartMins % 60}m
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </>
          )}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <header className="bg-white border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-medium tracking-tight">Reports</h1>
            <p className="text-xs text-muted-foreground mt-1">Generate scribe activity reports</p>
          </div>
          <Button
            data-testid="back-to-dashboard-button"
            variant="ghost"
            className="rounded-sm"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        <Tabs defaultValue="report1" className="space-y-6">
          <TabsList className="bg-white border border-border rounded-sm p-1">
            <TabsTrigger value="report1" className="rounded-sm" data-testid="report1-tab">
              <FileText className="h-4 w-4 mr-2" />
              Project Report
            </TabsTrigger>
            <TabsTrigger value="report3" className="rounded-sm" data-testid="report3-tab">
              <FileText className="h-4 w-4 mr-2" />
              User Report
            </TabsTrigger>
            <TabsTrigger value="report2" className="rounded-sm" data-testid="report2-tab">
              <FileText className="h-4 w-4 mr-2" />
              Monthly Report
            </TabsTrigger>
            <TabsTrigger value="mis" className="rounded-sm" data-testid="mis-tab">
              <FileText className="h-4 w-4 mr-2" />
              MIS Report
            </TabsTrigger>
            <TabsTrigger value="mis2" className="rounded-sm" data-testid="mis2-tab">
              <FileText className="h-4 w-4 mr-2" />
              MIS Report 2
            </TabsTrigger>
          </TabsList>

          {/* Report 1 */}
          <TabsContent value="report1" className="space-y-6">
            <div className="bg-white border border-border shadow-sm rounded-sm p-6">
              <h2 className="text-xl font-medium mb-6">Project Report</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Month
                  </Label>
                  <Select
                    value={report1Filters.month.toString()}
                    onValueChange={(value) =>
                      setReport1Filters({ ...report1Filters, month: parseInt(value) })
                    }
                  >
                    <SelectTrigger data-testid="report1-month-select" className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((month) => (
                        <SelectItem key={month.value} value={month.value.toString()}>
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Year
                  </Label>
                  <Select
                    value={report1Filters.year.toString()}
                    onValueChange={(value) =>
                      setReport1Filters({ ...report1Filters, year: parseInt(value) })
                    }
                  >
                    <SelectTrigger data-testid="report1-year-select" className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Project
                  </Label>
                  <Select
                    value={report1Filters.project}
                    onValueChange={(value) => setReport1Filters({ ...report1Filters, project: value })}
                  >
                    <SelectTrigger data-testid="report1-project-select" className="rounded-sm">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.project_name}>
                          {project.project_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 flex items-end">
                  <Button
                    data-testid="generate-report1-button"
                    onClick={generateReport1}
                    className="w-full h-10 rounded-sm bg-primary hover:bg-primary/90"
                  >
                    Generate Report
                  </Button>
                </div>
              </div>

              {report1Data.length > 0 && (
                <div className="flex justify-end gap-2 mb-4">
                  <Button
                    data-testid="export-report1-pdf-button"
                    variant="outline"
                    size="sm"
                    className="rounded-sm"
                    onClick={() => exportToPDF(report1Data, 'Project_Report', false, report1Summary)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export PDF
                  </Button>
                  <Button
                    data-testid="export-report1-csv-button"
                    variant="outline"
                    size="sm"
                    className="rounded-sm"
                    onClick={() => exportToCSV(report1Data, 'project_report')}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              )}
            </div>

            <div className="bg-white border border-border shadow-sm rounded-sm p-6">
              <h3 className="text-lg font-medium mb-4">Report Results</h3>
              
              {report1Summary && report1Data.length > 0 && (
                <div className="mb-6 p-4 bg-primary/5 rounded-sm border border-primary/20">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                        Total Entries
                      </p>
                      <p className="text-2xl font-semibold text-primary">
                        {report1Summary.count}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                        Total Duration
                      </p>
                      <p className="text-2xl font-semibold text-primary">
                        {report1Summary.totalDurationHours}h {report1Summary.totalDurationMins}m
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                        Total Pre-charting
                      </p>
                      <p className="text-2xl font-semibold text-primary">
                        {report1Summary.totalPreChartHours}h {report1Summary.totalPreChartMins}m
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-primary/20">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                      Combined Total (Duration + Pre-charting)
                    </p>
                    <p className="text-3xl font-bold text-primary">
                      {report1Summary.totalDecimal} hours
                    </p>
                  </div>
                </div>
              )}
              
              <div className="overflow-x-auto">
                <ReportTable data={report1Data} />
              </div>
            </div>
          </TabsContent>

          {/* Report 3 - User Report */}
          <TabsContent value="report3" className="space-y-6">
            <div className="bg-white border border-border shadow-sm rounded-sm p-6">
              <h2 className="text-xl font-medium mb-6">User Report</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Month
                  </Label>
                  <Select
                    value={report3Filters.month.toString()}
                    onValueChange={(value) =>
                      setReport3Filters({ ...report3Filters, month: parseInt(value) })
                    }
                  >
                    <SelectTrigger data-testid="report3-month-select" className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((month) => (
                        <SelectItem key={month.value} value={month.value.toString()}>
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Year
                  </Label>
                  <Select
                    value={report3Filters.year.toString()}
                    onValueChange={(value) =>
                      setReport3Filters({ ...report3Filters, year: parseInt(value) })
                    }
                  >
                    <SelectTrigger data-testid="report3-year-select" className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    User
                  </Label>
                  <Select
                    value={report3Filters.user}
                    onValueChange={(value) => setReport3Filters({ ...report3Filters, user: value })}
                  >
                    <SelectTrigger data-testid="report3-user-select" className="rounded-sm">
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.user_name}>
                          {user.user_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 flex items-end">
                  <Button
                    data-testid="generate-report3-button"
                    onClick={generateReport3}
                    className="w-full h-10 rounded-sm bg-primary hover:bg-primary/90"
                  >
                    Generate Report
                  </Button>
                </div>
              </div>

              {report3Data.length > 0 && (
                <div className="flex justify-end gap-2 mb-4">
                  <Button
                    data-testid="export-report3-pdf-button"
                    variant="outline"
                    size="sm"
                    className="rounded-sm"
                    onClick={() => exportToPDF(report3Data, 'User_Report', false, report3Summary)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export PDF
                  </Button>
                  <Button
                    data-testid="export-report3-csv-button"
                    variant="outline"
                    size="sm"
                    className="rounded-sm"
                    onClick={() => exportToCSV(report3Data, 'user_report')}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              )}
            </div>

            <div className="bg-white border border-border shadow-sm rounded-sm p-6">
              <h3 className="text-lg font-medium mb-4">Report Results</h3>
              
              {report3Summary && report3Data.length > 0 && (
                <div className="mb-6 p-4 bg-primary/5 rounded-sm border border-primary/20">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                        Total Entries
                      </p>
                      <p className="text-2xl font-semibold text-primary">
                        {report3Summary.count}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                        Total Duration
                      </p>
                      <p className="text-2xl font-semibold text-primary">
                        {report3Summary.totalDurationHours}h {report3Summary.totalDurationMins}m
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                        Total Pre-charting
                      </p>
                      <p className="text-2xl font-semibold text-primary">
                        {report3Summary.totalPreChartHours}h {report3Summary.totalPreChartMins}m
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-primary/20">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                      Combined Total (Duration + Pre-charting)
                    </p>
                    <p className="text-3xl font-bold text-primary">
                      {report3Summary.totalDecimal} hours
                    </p>
                  </div>
                </div>
              )}
              
              <div className="overflow-x-auto">
                <ReportTable data={report3Data} />
              </div>
            </div>
          </TabsContent>

          {/* Report 2 - Monthly Report */}
          <TabsContent value="report2" className="space-y-6">
            <div className="bg-white border border-border shadow-sm rounded-sm p-6">
              <h2 className="text-xl font-medium mb-6">Monthly Report</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Month
                  </Label>
                  <Select
                    value={report2Filters.month.toString()}
                    onValueChange={(value) =>
                      setReport2Filters({ ...report2Filters, month: parseInt(value) })
                    }
                  >
                    <SelectTrigger data-testid="report2-month-select" className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((month) => (
                        <SelectItem key={month.value} value={month.value.toString()}>
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Year
                  </Label>
                  <Select
                    value={report2Filters.year.toString()}
                    onValueChange={(value) =>
                      setReport2Filters({ ...report2Filters, year: parseInt(value) })
                    }
                  >
                    <SelectTrigger data-testid="report2-year-select" className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 flex items-end">
                  <Button
                    data-testid="generate-report2-button"
                    onClick={generateReport2}
                    className="w-full h-10 rounded-sm bg-primary hover:bg-primary/90"
                  >
                    Generate Report
                  </Button>
                </div>
              </div>

              {report2Data.length > 0 && (
                <div className="flex justify-end gap-2 mb-4">
                  <Button
                    data-testid="export-report2-pdf-button"
                    variant="outline"
                    size="sm"
                    className="rounded-sm"
                    onClick={() => exportToPDF(report2Data, 'Monthly_Summary', true, report2Summary)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export PDF
                  </Button>
                  <Button
                    data-testid="export-report2-excel-button"
                    variant="outline"
                    size="sm"
                    className="rounded-sm"
                    onClick={() => exportMonthlyToExcel(report2Data, 'Monthly_Report', report2Summary)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export Excel
                  </Button>
                </div>
              )}
            </div>

            {report2Data.length > 0 && (
              <div className="bg-white border border-border shadow-sm rounded-sm p-6">
                <h3 className="text-lg font-medium mb-4">Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="p-4 bg-muted/30 rounded-sm">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                      Total Entries
                    </p>
                    <p className="text-3xl font-semibold text-primary">
                      {report2Data.length}
                    </p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-sm">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                      Total Duration
                    </p>
                    <p className="text-3xl font-semibold text-primary">
                      {report2Summary.totalDurationHours}h {report2Summary.totalDurationMins}m
                    </p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-sm">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                      Total Pre-charting
                    </p>
                    <p className="text-3xl font-semibold text-primary">
                      {report2Summary.totalPreChartHours}h {report2Summary.totalPreChartMins}m
                    </p>
                  </div>
                </div>
                <div className="p-4 bg-primary/5 rounded-sm border border-primary/20">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                    Combined Total (Duration + Pre-charting)
                  </p>
                  <p className="text-4xl font-bold text-primary">
                    {((report2Summary.totalDurationHours * 60 + report2Summary.totalDurationMins + report2Summary.totalPreChartHours * 60 + report2Summary.totalPreChartMins) / 60).toFixed(2)} hours
                  </p>
                </div>
              </div>
            )}

            <div className="bg-white border border-border shadow-sm rounded-sm p-6">
              <h3 className="text-lg font-medium mb-4">Report Results</h3>
              <div className="overflow-x-auto">
                <ReportTable 
                  data={report2Data} 
                  showSubtotals={true} 
                  subtotals={report2Summary.projectSubtotals || {}} 
                />
              </div>
            </div>

            {report2Summary.projectsWithNoData && report2Summary.projectsWithNoData.length > 0 && (
              <div className="bg-white border border-border shadow-sm rounded-sm p-6">
                <h3 className="text-lg font-medium mb-4">Projects with No Data</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  The following projects had no scribe entries for the selected month and year:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {report2Summary.projectsWithNoData.map((project, index) => (
                    <div 
                      key={index}
                      className="p-3 bg-muted/30 rounded-sm border border-border flex items-center gap-2"
                    >
                      <div className="w-2 h-2 rounded-full bg-muted-foreground/50"></div>
                      <span className="text-sm">{project}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-sm">
                  <p className="text-sm text-blue-800">
                    <strong>{report2Summary.projectsWithNoData.length}</strong> project(s) with no activity during this period
                  </p>
                </div>
              </div>
            )}
          </TabsContent>

          {/* MIS Report — combined Daily + Cumulative graphs only */}
          <TabsContent value="mis" className="space-y-6">
            <div className="bg-white border border-border shadow-sm rounded-sm p-6">
              <h2 className="text-xl font-medium mb-2">MIS Report</h2>
              <p className="text-xs text-muted-foreground mb-6">
                Pick a date for the Daily graph and a month/year for the Cumulative graph, then generate.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Daily — Date
                  </Label>
                  <Input
                    data-testid="mis-daily-date-input"
                    type="date"
                    value={report4Filters.date}
                    onChange={(e) =>
                      setReport4Filters({ ...report4Filters, date: e.target.value })
                    }
                    className="rounded-sm h-10"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Cumulative — Month
                  </Label>
                  <Select
                    value={String(report5Filters.month)}
                    onValueChange={(v) =>
                      setReport5Filters({ ...report5Filters, month: parseInt(v) })
                    }
                  >
                    <SelectTrigger data-testid="mis-month-select" className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((m) => (
                        <SelectItem key={m.value} value={String(m.value)}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Cumulative — Year
                  </Label>
                  <Select
                    value={String(report5Filters.year)}
                    onValueChange={(v) =>
                      setReport5Filters({ ...report5Filters, year: parseInt(v) })
                    }
                  >
                    <SelectTrigger data-testid="mis-year-select" className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={String(y)}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    data-testid="generate-mis-report-button"
                    onClick={async () => {
                      await generateReport4();
                      await generateReport5();
                      await generateReport6();
                    }}
                    className="w-full h-10 rounded-sm bg-primary hover:bg-primary/90"
                  >
                    Generate Report
                  </Button>
                </div>
              </div>
            </div>

            {report4Rows.length > 0 && (
              <div
                className="bg-white border border-border border-l-4 border-l-indigo-600 shadow-sm rounded-sm p-6"
                ref={report4ChartRef}
                data-testid="mis-daily-chart-card"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Daily Report Graph — {report4Filters.date}
                </h3>
                <p className="text-xs text-muted-foreground mb-5">
                  Duration + Pre-charting hours per user + project for the selected date.
                </p>
                <div style={{ width: '100%', height: Math.max(300, report4Rows.length * 32) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={(() => {
                        const byUserProject = {};
                        report4Rows.forEach((r) => {
                          if (r.empty) {
                            const key = `idle|${r.user}`;
                            if (!byUserProject[key]) {
                              byUserProject[key] = { label: r.user, durationHrs: 0, preChartHrs: 0 };
                            }
                            return;
                          }
                          const key = `${r.user}|${r.project}`;
                          if (!byUserProject[key]) {
                            byUserProject[key] = {
                              label: `${r.user}||${r.project}`,
                              durationHrs: 0,
                              preChartHrs: 0,
                            };
                          }
                          const d = calculateTimeDifference(r.start_time, r.end_time);
                          byUserProject[key].durationHrs += (d.hours * 60 + d.mins) / 60;
                          byUserProject[key].preChartHrs +=
                            (r.pre_charting_Hours * 60 + r.pre_charting_mins) / 60;
                        });
                        return Object.values(byUserProject).map((x) => ({
                          user: x.label,
                          Duration: Number(x.durationHrs.toFixed(2)),
                          'Pre-charting': Number(x.preChartHrs.toFixed(2)),
                          total: Number((x.durationHrs + x.preChartHrs).toFixed(2)),
                        }));
                      })()}
                      margin={{ top: 12, right: 70, left: 20, bottom: 16 }}
                      barCategoryGap={10}
                    >
                      <CartesianGrid strokeDasharray="4 4" stroke="#eef2f7" horizontal={false} />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tickLine={false}
                        label={{ value: 'Hours', position: 'insideBottom', offset: -6, fontSize: 11, fill: '#6b7280' }}
                      />
                      <YAxis
                        type="category"
                        dataKey="user"
                        interval={0}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tick={<UserProjectTick />}
                        width={220}
                      />
                      <Tooltip
                        formatter={(v) => `${v} hrs`}
                        labelFormatter={(label) => String(label).replace('||', ' — ')}
                        contentStyle={misTooltipContentStyle}
                        cursor={misTooltipCursor}
                      />
                      <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                      <Bar dataKey="Duration" stackId="hrs" fill="#4F46E5" />
                      <Bar dataKey="Pre-charting" stackId="hrs" fill="#F59E0B" radius={[0, 6, 6, 0]}>
                        <LabelList
                          dataKey="total"
                          position="right"
                          formatter={(v) => (v > 0 ? `${v} hrs` : '')}
                          style={{ fontSize: 11, fill: '#111827', fontWeight: 600 }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {report5Rows.length > 0 && (
              <div
                className="bg-white border border-border border-l-4 border-l-indigo-600 shadow-sm rounded-sm p-6"
                ref={report5ChartRef}
                data-testid="mis-cumul-chart-card"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Cumulative Report Graph — {months.find((m) => m.value === parseInt(report5Filters.month))?.label} {report5Filters.year}
                </h3>
                <p className="text-xs text-muted-foreground mb-5">
                  Cumulative Duration + Pre-charting hours per user + project for the selected month.
                </p>
                <div style={{ width: '100%', height: Math.max(300, report5Rows.length * 32) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={report5Rows.map((r) => {
                        const durH = r.empty ? 0 : r.durationMins / 60;
                        const preH = r.empty ? 0 : r.preChartMins / 60;
                        return {
                          user: r.empty ? r.user : `${r.user}||${r.project}`,
                          Duration: Number(durH.toFixed(2)),
                          'Pre-charting': Number(preH.toFixed(2)),
                          total: Number((durH + preH).toFixed(2)),
                        };
                      })}
                      margin={{ top: 12, right: 70, left: 20, bottom: 16 }}
                      barCategoryGap={10}
                    >
                      <CartesianGrid strokeDasharray="4 4" stroke="#eef2f7" horizontal={false} />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tickLine={false}
                        label={{ value: 'Hours', position: 'insideBottom', offset: -6, fontSize: 11, fill: '#6b7280' }}
                      />
                      <YAxis
                        type="category"
                        dataKey="user"
                        interval={0}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tick={<UserProjectTick />}
                        width={220}
                      />
                      <Tooltip
                        formatter={(v) => `${v} hrs`}
                        labelFormatter={(label) => String(label).replace('||', ' — ')}
                        contentStyle={misTooltipContentStyle}
                        cursor={misTooltipCursor}
                      />
                      <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                      <Bar dataKey="Duration" stackId="hrs" fill="#4F46E5" />
                      <Bar dataKey="Pre-charting" stackId="hrs" fill="#F59E0B" radius={[0, 6, 6, 0]}>
                        <LabelList
                          dataKey="total"
                          position="right"
                          formatter={(v) => (v > 0 ? `${v} hrs` : '')}
                          style={{ fontSize: 11, fill: '#111827', fontWeight: 600 }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {report6Rows.length > 0 && (
              <div
                className="bg-white border border-border border-l-4 border-l-indigo-600 shadow-sm rounded-sm p-6"
                ref={report6ChartRef}
                data-testid="mis-project-chart-card"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Project Wise Cumulative — {months.find((m) => m.value === parseInt(report5Filters.month))?.label} {report5Filters.year}
                </h3>
                <p className="text-xs text-muted-foreground mb-5">
                  Cumulative Duration + Pre-charting hours per active project for the selected month.
                </p>
                <div style={{ width: '100%', height: Math.max(300, report6Rows.length * 32) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={report6Rows.map((r) => {
                        const durH = r.empty ? 0 : r.durationMins / 60;
                        const preH = r.empty ? 0 : r.preChartMins / 60;
                        return {
                          project: r.project,
                          Duration: Number(durH.toFixed(2)),
                          'Pre-charting': Number(preH.toFixed(2)),
                          total: Number((durH + preH).toFixed(2)),
                        };
                      })}
                      margin={{ top: 12, right: 70, left: 20, bottom: 16 }}
                      barCategoryGap={10}
                    >
                      <CartesianGrid strokeDasharray="4 4" stroke="#eef2f7" horizontal={false} />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tickLine={false}
                        label={{ value: 'Hours', position: 'insideBottom', offset: -6, fontSize: 11, fill: '#6b7280' }}
                      />
                      <YAxis
                        type="category"
                        dataKey="project"
                        interval={0}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tick={{ fontSize: 11, fill: '#7C3AED', fontStyle: 'italic' }}
                        width={200}
                      />
                      <Tooltip
                        formatter={(v) => `${v} hrs`}
                        contentStyle={misTooltipContentStyle}
                        cursor={misTooltipCursor}
                      />
                      <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                      <Bar dataKey="Duration" stackId="hrs" fill="#4F46E5" />
                      <Bar dataKey="Pre-charting" stackId="hrs" fill="#F59E0B" radius={[0, 6, 6, 0]}>
                        <LabelList
                          dataKey="total"
                          position="right"
                          formatter={(v) => (v > 0 ? `${v} hrs` : '')}
                          style={{ fontSize: 11, fill: '#111827', fontWeight: 600 }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {(report4Rows.length > 0 || report5Rows.length > 0 || report6Rows.length > 0) && (
              <div className="flex justify-end">
                <Button
                  data-testid="export-mis-pdf-button"
                  variant="outline"
                  className="rounded-sm"
                  onClick={exportMISToPDF}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Save as PDF
                </Button>
              </div>
            )}
          </TabsContent>

          {/* MIS Report 2 — Scribing Utilisation */}
          <TabsContent value="mis2" className="space-y-6">
            <div className="bg-white border border-border shadow-sm rounded-sm p-6">
              <h2 className="text-xl font-medium mb-2">MIS Report 2 — Scribing Utilisation</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Duration + pre-charting hours per scribe vs a dynamic benchmark of
                8 hrs × working days (Mon–Fri) in the selected month. Sorted high-to-low.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Month
                  </Label>
                  <Select
                    value={String(report7Filters.month)}
                    onValueChange={(v) =>
                      setReport7Filters({ ...report7Filters, month: parseInt(v) })
                    }
                  >
                    <SelectTrigger data-testid="mis2-month-select" className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((m) => (
                        <SelectItem key={m.value} value={String(m.value)}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Year
                  </Label>
                  <Select
                    value={String(report7Filters.year)}
                    onValueChange={(v) =>
                      setReport7Filters({ ...report7Filters, year: parseInt(v) })
                    }
                  >
                    <SelectTrigger data-testid="mis2-year-select" className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={String(y)}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    End Production Date
                  </Label>
                  <Input
                    type="date"
                    value={report7Filters.date}
                    onChange={(e) =>
                      setReport7Filters({ ...report7Filters, date: e.target.value })
                    }
                    className="rounded-sm h-10"
                    data-testid="mis2-date-input"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    data-testid="generate-mis2-report-button"
                    onClick={generateReport7}
                    className="w-full h-10 rounded-sm bg-primary hover:bg-primary/90"
                  >
                    Generate Report
                  </Button>
                </div>
              </div>
            </div>

            {report7Rows.length > 0 && report7Summary && (
              <div
                className="bg-white border border-border border-l-4 border-l-indigo-600 shadow-sm rounded-sm p-6"
                ref={report7ChartRef}
                data-testid="mis2-chart-card"
              >
                <div className="mb-1">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Scribing utilisation — {months.find((m) => m.value === report7Summary.month)?.label} {report7Summary.year} (MTD to {report7Summary.asOfLabel})
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Duration + pre-charting hours per scribe vs a dynamic
                    benchmark of {report7Summary.benchmarkPerScribe}h (8 hrs × {report7Summary.workingDays} working days Mon–Fri)
                  </p>
                </div>

                {/* Summary tiles */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 mb-6">
                  <div className="bg-gray-50 border border-gray-100 rounded-sm p-4" data-testid="mis2-tile-mtd">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">MTD hours</div>
                    <div className="text-2xl font-bold text-gray-900">{report7Summary.mtdHours.toLocaleString()}</div>
                  </div>
                  <div className="bg-gray-50 border border-gray-100 rounded-sm p-4" data-testid="mis2-tile-capacity">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Capacity used</div>
                    <div className="text-2xl font-bold text-indigo-700">{report7Summary.capacityUsedPct}%</div>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      {report7Summary.totalScribes} scribes × 8h × {report7Summary.workingDays} working days
                    </div>
                  </div>
                  <div className="bg-gray-50 border border-gray-100 rounded-sm p-4" data-testid="mis2-tile-zero-mtd">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Zero-hour scribes MTD</div>
                    <div className="text-2xl font-bold text-red-600">{report7Summary.zeroMtd}</div>
                  </div>
                  <div className="bg-gray-50 border border-gray-100 rounded-sm p-4" data-testid="mis2-tile-zero-asof">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Zero on {report7Summary.asOfLabel}</div>
                    <div className="text-2xl font-bold text-amber-600">
                      {report7Summary.zeroOnAsOf} <span className="text-base font-medium text-muted-foreground">of {report7Summary.totalScribes}</span>
                    </div>
                  </div>
                </div>

                {/* Chart */}
                <div style={{ width: '100%', height: Math.max(400, report7Rows.length * 26) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={report7Rows}
                      margin={{ top: 16, right: 140, left: 20, bottom: 24 }}
                      barCategoryGap={4}
                    >
                      <CartesianGrid strokeDasharray="4 4" stroke="#eef2f7" horizontal={false} />
                      <XAxis
                        type="number"
                        domain={[0, (dataMax) => Math.max(dataMax, report7Summary.benchmarkPerScribe) * 1.15]}
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tickLine={false}
                        label={{ value: 'Total Production Hours (Duration + Pre-charting)', position: 'insideBottom', offset: -10, fontSize: 11, fill: '#374151' }}
                      />
                      <YAxis
                        type="category"
                        dataKey="label"
                        interval={0}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tick={{ fontSize: 11, fill: '#111827' }}
                        width={180}
                      />
                      <Tooltip
                        formatter={(v, name, entry) => [
                          `${v} hrs  ·  ${entry?.payload?.pct}% of ${report7Summary.benchmarkPerScribe}h benchmark`,
                          'Total Production Hours',
                        ]}
                        contentStyle={misTooltipContentStyle}
                        cursor={misTooltipCursor}
                      />
                      <ReferenceLine
                        x={report7Summary.benchmarkPerScribe}
                        stroke="#374151"
                        strokeDasharray="6 4"
                        label={{ value: `Benchmark ${report7Summary.benchmarkPerScribe}h`, position: 'top', fill: '#374151', fontSize: 11 }}
                      />
                      <Bar dataKey="hours" name="Total Production Hours" radius={[0, 4, 4, 0]}>
                        {report7Rows.map((r, i) => (
                          <Cell key={`c-${i}`} fill={r.fill} />
                        ))}
                        <LabelList
                          dataKey="barLabel"
                          position="right"
                          offset={8}
                          style={{ fontSize: 11, fill: '#111827', fontWeight: 500 }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend — bars use % of the dynamic benchmark to pick a colour band */}
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
                    Total Production Hours — colour band by % of {report7Summary.benchmarkPerScribe}h benchmark
                  </div>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-gray-700">
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-4 h-3 rounded-sm" style={{ backgroundColor: UTIL_COLORS.over }} />
                      Over 110% — verify logs
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-4 h-3 rounded-sm" style={{ backgroundColor: UTIL_COLORS.onTarget }} />
                      80–110% — on target
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-4 h-3 rounded-sm" style={{ backgroundColor: UTIL_COLORS.mid }} />
                      50–80%
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-4 h-3 rounded-sm" style={{ backgroundColor: UTIL_COLORS.under }} />
                      Under 50%
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-4 h-3 rounded-sm" style={{ backgroundColor: UTIL_COLORS.zero }} />
                      Zero hours
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-6 border-t border-dashed border-gray-600" />
                      {report7Summary.benchmarkPerScribe}h benchmark
                    </div>
                  </div>
                </div>

                <p className="text-[11px] text-muted-foreground mt-3">
                  Figures in brackets after a scribe name = number of projects the scribe is split across.
                </p>
              </div>
            )}

            {report7Rows.length > 0 && (
              <div className="flex justify-end">
                <Button
                  data-testid="export-mis2-pdf-button"
                  variant="outline"
                  className="rounded-sm"
                  onClick={exportReport7ToPDF}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Save as PDF
                </Button>
              </div>
            )}
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
};

export default ReportsPanel;
